import { useEffect, useState } from "react";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
    FormActions,
} from "../../components/admin/FormPrimitives";
import {
    BilingualField,
    BilingualListField,
} from "../../components/admin/BilingualField";

function PracticeAreaCard({ area, onSaved }) {
    const [form, setForm] = useState({
        title: area.title,
        description: area.description,
        example_situations: area.example_situations,
        order: area.order,
    });
    const [state, setState] = useState({ status: "idle", error: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        setState({ status: "submitting", error: "" });
        try {
            const { data } = await adminApi.put(
                `/admin/practice-areas/${area.slug}`,
                form,
            );
            setState({ status: "success", error: "" });
            onSaved(data);
            setTimeout(
                () =>
                    setState((s) =>
                        s.status === "success"
                            ? { status: "idle", error: "" }
                            : s,
                    ),
                2500,
            );
        } catch (err) {
            setState({ status: "error", error: extractApiError(err) });
        }
    };

    return (
        <SectionCard
            title={form.title?.fr || area.slug}
            description={`slug : ${area.slug}`}
            testId={`practice-card-${area.slug}`}
        >
            <form onSubmit={onSubmit}>
                <BilingualField
                    label="Titre"
                    idPrefix={`practice-title-${area.slug}`}
                    value={form.title}
                    onChange={(v) => setForm({ ...form, title: v })}
                />
                <BilingualField
                    label="Description (~150 mots)"
                    type="textarea"
                    rows={6}
                    idPrefix={`practice-desc-${area.slug}`}
                    value={form.description}
                    onChange={(v) => setForm({ ...form, description: v })}
                />
                <BilingualListField
                    label="Situations types"
                    idPrefix={`practice-situations-${area.slug}`}
                    hint="Une situation par ligne — même nombre de lignes en FR et en EN."
                    value={form.example_situations}
                    onChange={(v) =>
                        setForm({ ...form, example_situations: v })
                    }
                />
                <div className="mb-4 max-w-[200px]">
                    <div className="eyebrow mb-1">Ordre d'affichage</div>
                    <input
                        type="number"
                        min={1}
                        className="field-box"
                        value={form.order}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                order: parseInt(e.target.value, 10) || 1,
                            })
                        }
                        data-testid={`practice-order-${area.slug}`}
                    />
                </div>
                <FormActions
                    saving={state.status === "submitting"}
                    status={state.status}
                    error={state.error}
                    testId={`practice-form-${area.slug}`}
                />
            </form>
        </SectionCard>
    );
}

export default function AdminPracticeAreas() {
    const [areas, setAreas] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await adminApi.get("/admin/practice-areas");
                setAreas(data || []);
            } finally {
                setLoaded(true);
            }
        })();
    }, []);

    const onSaved = (updated) => {
        setAreas((prev) =>
            prev
                .map((a) => (a.slug === updated.slug ? updated : a))
                .sort((a, b) => a.order - b.order),
        );
    };

    return (
        <div data-testid="admin-practice-page">
            <AdminPageHeader
                title="Domaines d'intervention"
                subtitle="Sept domaines, éditables individuellement (titre, description, situations types, ordre)."
            />
            {!loaded ? (
                <SectionCard title="Chargement…">…</SectionCard>
            ) : (
                areas.map((a) => (
                    <PracticeAreaCard key={a.slug} area={a} onSaved={onSaved} />
                ))
            )}
        </div>
    );
}
